import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

// NOTA: o pedido original citava "/afiliado/inscricao" pro cadastro de afiliado,
// mas a rota real registrada em App.js é "/afiliado/cadastro" (AffiliateSignup) —
// usamos a rota que de fato existe.
const LINK_GROUPS = [
    {
        title: "Cadastros e Acesso",
        links: [
            { title: "Self-signup (novo salão)", path: "/register", desc: "Link para novos salões se cadastrarem" },
            { title: "Self-signup alternativo", path: "/cadastro", desc: "Link alternativo de cadastro" },
            { title: "Login do sistema", path: "/", desc: "Página de login para salões cadastrados" },
            { title: "Portal de afiliados", path: "/afiliado", desc: "Acesso ao portal do afiliado" },
        ],
    },
    {
        title: "Programa de Afiliados",
        links: [
            { title: "Cadastro de afiliado", path: "/afiliado/cadastro", desc: "Link para novos afiliados se inscreverem" },
            { title: "Portal do afiliado (login)", path: "/afiliado", desc: "Onde afiliados acessam comissões e link" },
            { title: "Exemplo de link com ref", path: "/?ref=exemplo", desc: "Como fica o link de indicação" },
        ],
    },
    {
        title: "Páginas Públicas",
        links: [
            { title: "Termos de uso", path: "/termos", desc: "Página pública dos termos de uso" },
            { title: "Política de privacidade", path: "/privacidade", desc: "Página pública da política de privacidade" },
        ],
    },
    {
        title: "Super Admin",
        links: [
            { title: "Painel administrativo", path: "/admin", desc: "Acesso ao painel de administração" },
        ],
    },
];

/**
 * AdminLinks — hub "Links" do Painel Mestre: reúne todas as URLs públicas/
 * internas da plataforma num só lugar, cada uma copiável com um clique.
 */
const AdminLinks = () => {
    const [view, setView] = useState("hub");

    if (view === "hub") {
        return (
            <div className="space-y-6" data-testid="admin-links-hub">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-400">Plataforma</div>
                    <h1 className="salon-heading text-3xl text-white mt-1">Links</h1>
                    <p className="text-sm text-slate-400 mt-1">URLs importantes da plataforma, prontas pra compartilhar.</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
                    <button
                        data-testid="admin-links-card"
                        onClick={() => setView("links")}
                        className="text-left rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-5 hover:border-indigo-500/40 transition-all group"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div className="h-11 w-11 rounded-xl bg-indigo-500/10 flex items-center justify-center">
                                <i className="fa-solid fa-link text-lg text-indigo-300"></i>
                            </div>
                            <i className="fa-solid fa-arrow-right text-slate-600 group-hover:text-indigo-300 group-hover:translate-x-1 transition-all"></i>
                        </div>
                        <div className="text-white text-base font-medium mb-1">Links do Sistema</div>
                        <div className="text-xs text-slate-500">Todos os links importantes para compartilhar</div>
                    </button>
                </div>
            </div>
        );
    }

    return <LinksPage onBack={() => setView("hub")} />;
};

const LinksPage = ({ onBack }) => {
    const [copied, setCopied] = useState(false);
    const base = typeof window !== "undefined" ? window.location.origin : "";

    const flashCopied = useCallback(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }, []);

    const copyText = useCallback((text) => {
        navigator.clipboard.writeText(text);
        flashCopied();
    }, [flashCopied]);

    const handleCopy = (path) => copyText(`${base}${path}`);

    return (
        <div className="space-y-6" data-testid="admin-links-page">
            <button
                data-testid="admin-links-back"
                onClick={onBack}
                className="h-10 w-10 rounded-xl border border-white/10 hover:border-indigo-500/40 text-slate-400 hover:text-indigo-300 flex items-center justify-center transition-all"
            >
                <i className="fa-solid fa-arrow-left"></i>
            </button>

            <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-400">Plataforma · Links</div>
                <h1 className="salon-heading text-3xl text-white mt-1">Links do Sistema</h1>
                <p className="text-sm text-slate-400 mt-1">Clique em qualquer card para copiar a URL.</p>
            </div>

            <div className="space-y-8 max-w-3xl">
                {LINK_GROUPS.map((group) => (
                    <div key={group.title}>
                        <h2 className="text-[11px] uppercase tracking-[0.25em] text-indigo-300 font-bold mb-3">
                            {group.title}
                        </h2>
                        <div className="space-y-3">
                            {group.links.map((link) => {
                                const url = `${base}${link.path}`;
                                const testSlug = link.path === "/" ? "root" : link.path.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "");
                                return (
                                    <button
                                        key={link.path}
                                        data-testid={`admin-link-${testSlug}`}
                                        onClick={() => handleCopy(link.path)}
                                        className="w-full text-left rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-4 hover:border-indigo-500/40 hover:shadow-[0_0_30px_-15px_rgba(99,102,241,0.6)] transition-all flex items-center gap-4 group"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="text-white font-semibold text-base">{link.title}</div>
                                            <div className="text-[11px] text-slate-500 font-mono truncate mt-1">{url}</div>
                                            <div className="text-xs text-slate-400 mt-1.5">{link.desc}</div>
                                        </div>
                                        <i className="fa-solid fa-copy text-slate-600 group-hover:text-indigo-300 transition-colors shrink-0"></i>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}

                <DirectInvitesSection copyText={copyText} />
            </div>

            {copied && (
                <div
                    data-testid="admin-links-copied-toast"
                    className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[90] px-5 py-3 rounded-xl bg-slate-900 border border-emerald-500/40 shadow-2xl flex items-center gap-2 text-sm text-white animate-in fade-in duration-200"
                >
                    <span>✅</span>
                    Link copiado!
                </div>
            )}
        </div>
    );
};

/**
 * DirectInvitesSection — "Convites Diretos": links de rastreio criados pelo
 * Super Admin, 100% separados do programa de afiliados (coleção invite_links,
 * sem comissão). Cada cadastro feito por um link grava `invited_via_code` no
 * tenant, e a contagem aparece aqui.
 */
const DirectInvitesSection = ({ copyText }) => {
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [label, setLabel] = useState("");
    const [creating, setCreating] = useState(false);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        try {
            const list = await api.get("/admin/invite-links");
            setInvites(Array.isArray(list) ? list : []);
        } catch (e) {
            toast.error("Falha ao carregar convites", { description: e.message });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const create = async () => {
        const clean = label.trim();
        if (clean.length < 2) { toast.error("Dê um nome ao convite"); return; }
        setCreating(true);
        try {
            await api.post("/admin/invite-links", { label: clean });
            setLabel("");
            toast.success("Convite criado");
            await load();
        } catch (e) {
            toast.error("Falha ao criar convite", { description: e.message });
        } finally {
            setCreating(false);
        }
    };

    const deactivate = async (inv) => {
        if (!window.confirm(`Desativar o convite "${inv.label}"? O link para de funcionar para novos cadastros, mas o histórico de contagem é mantido.`)) return;
        setBusyId(inv.id);
        try {
            await api.del(`/admin/invite-links/${inv.id}`);
            toast.success("Convite desativado");
            await load();
        } catch (e) {
            toast.error("Falha ao desativar", { description: e.message });
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div data-testid="admin-invites-section">
            <h2 className="text-[11px] uppercase tracking-[0.25em] text-indigo-300 font-bold mb-1">
                Convites Diretos
            </h2>
            <p className="text-xs text-slate-500 mb-3">
                Links de rastreio para acompanhar de onde vêm os cadastros. Independente do programa de afiliados — não gera comissão.
            </p>

            <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <input
                    data-testid="admin-invite-label-input"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") create(); }}
                    placeholder='Nome do convite (ex: "Convite - Maria")'
                    className="flex-1 bg-slate-900/60 border border-white/10 focus:border-indigo-500/50 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none transition-all"
                />
                <button
                    data-testid="admin-invite-create-btn"
                    onClick={create}
                    disabled={creating || label.trim().length < 2}
                    className="rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white font-semibold px-5 py-2.5 text-sm disabled:opacity-40 transition-all shrink-0"
                >
                    {creating ? <i className="fa-solid fa-spinner fa-spin"></i> : "Criar convite"}
                </button>
            </div>

            {loading ? (
                <div className="text-sm text-slate-500 py-4">Carregando convites…</div>
            ) : invites.length === 0 ? (
                <div className="text-sm text-slate-500 rounded-2xl border border-dashed border-white/10 p-6 text-center">
                    Nenhum convite criado ainda.
                </div>
            ) : (
                <div className="space-y-3">
                    {invites.map((inv) => {
                        const url = inv.url || `https://kinng.app/register?invite=${inv.code}`;
                        return (
                            <div
                                key={inv.id}
                                data-testid={`admin-invite-${inv.code}`}
                                className={`rounded-2xl border p-4 transition-all ${
                                    inv.active
                                        ? "border-white/5 bg-slate-900/40 backdrop-blur-xl"
                                        : "border-white/5 bg-slate-900/20 opacity-50"
                                }`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-white font-semibold text-base">{inv.label}</span>
                                            {!inv.active && (
                                                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-700/60 text-slate-300 border border-white/10">
                                                    Inativo
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[11px] text-slate-500 font-mono truncate mt-1">{url}</div>
                                        <div className="text-xs text-slate-400 mt-1.5">
                                            <span className="text-indigo-300 font-semibold">{inv.signups}</span>{" "}
                                            {inv.signups === 1 ? "cadastro" : "cadastros"} por este link
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <button
                                            data-testid={`admin-invite-copy-${inv.code}`}
                                            onClick={() => copyText(url)}
                                            title="Copiar link"
                                            className="h-9 w-9 rounded-xl border border-white/10 hover:border-indigo-500/40 text-slate-400 hover:text-indigo-300 flex items-center justify-center transition-all"
                                        >
                                            <i className="fa-solid fa-copy text-xs"></i>
                                        </button>
                                        {inv.active && (
                                            <button
                                                data-testid={`admin-invite-deactivate-${inv.code}`}
                                                onClick={() => deactivate(inv)}
                                                disabled={busyId === inv.id}
                                                title="Desativar convite"
                                                className="h-9 w-9 rounded-xl border border-white/10 hover:border-red-500/40 text-slate-400 hover:text-red-300 flex items-center justify-center transition-all disabled:opacity-40"
                                            >
                                                <i className={`fa-solid ${busyId === inv.id ? "fa-spinner fa-spin" : "fa-ban"} text-xs`}></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default AdminLinks;
